import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import {
  Sheet,
  SheetContent as SheetContentBase,
  SheetClose as SheetCloseBase,
} from "./ui/sheet";

const SheetContent = SheetContentBase as React.FC<any>;
const SheetClose = SheetCloseBase as React.FC<any>;
import { nav } from "../content/landingContent";
import { scrollToId } from "../lib/lenisInstance";
import { useResourceImage } from "../lib/useResourceImage";
import { useContentField, useNavItems, useLinks } from "../lib/useContent";
import { trackLead } from "../lib/metaPixel";
import logo from "../assets/brand/logo-header.png";

export const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { src: logoSrc } = useResourceImage("header.logo", logo);
  const navItems = useNavItems("nav.itemsList", nav.items);
  const downloadCta = useContentField("nav.downloadCta", nav.downloadCta);
  const links = useLinks();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.06)] border-b border-neutral-200"
          : "bg-white/70 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 lg:h-20 flex items-center justify-between gap-4">
        <a
          href="#inicio"
          data-testid="header-logo-link"
          onClick={(e) => {
            e.preventDefault();
            handleNavClick("#inicio");
          }}
          className="flex items-center shrink-0 h-full py-2.5 lg:py-3 w-[130px] sm:w-[190px] md:w-[220px] lg:w-[280px] max-w-[45vw]"
          aria-label="MotorPRO Autos — Inicio"
        >
          <img
            src={logoSrc}
            alt="MotorPRO Autos"
            className="h-full max-h-12 sm:max-h-14 lg:max-h-16 w-full object-contain object-left scale-[3.2] origin-left"
          />
          {/* Reserved brand-area slot for a future slogan/badge — intentionally empty for now */}
          <span data-testid="brand-area-slogan-slot" className="sr-only" />
        </a>

        <nav className="hidden xl:flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.href}
              data-testid={`nav-link-${item.href.replace("#", "")}`}
              onClick={() => handleNavClick(item.href)}
              className="px-3 py-2 text-[13px] font-semibold text-neutral-700 hover:text-motor-red rounded-full transition-colors whitespace-nowrap"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={links.appUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-download-app-button"
            onClick={() => trackLead("header_download_desktop")}
            className="hidden sm:inline-flex items-center rounded-full bg-motor-red px-5 py-2.5 text-sm font-display font-semibold text-white uppercase tracking-wide hover:bg-motor-red-dark transition-colors shadow-[0_8px_22px_-6px_rgba(200,16,46,0.55)]"
          >
            {downloadCta}
          </a>

          <Sheet open={open} onOpenChange={setOpen}>
            <button
              data-testid="mobile-menu-open-button"
              onClick={() => setOpen(true)}
              className="xl:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-neutral-200 text-motor-ink"
              aria-label="Abrir menú"
            >
              <Menu className="h-5 w-5" />
            </button>
            <SheetContent side="right" className="w-[85%] sm:max-w-sm flex flex-col p-0">
              <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
                <img src={logoSrc} alt="MotorPRO Autos" className="h-12 w-auto object-contain scale-[2.8] origin-left ml-2" />
                <SheetClose
                  data-testid="mobile-menu-close-button"
                  className="h-10 w-10 flex items-center justify-center rounded-full border border-neutral-200"
                >
                  <X className="h-5 w-5" />
                </SheetClose>
              </div>
              <nav className="flex flex-col px-3 py-2 overflow-y-auto">
                {navItems.map((item) => (
                  <button
                    key={item.href}
                    data-testid={`mobile-nav-link-${item.href.replace("#", "")}`}
                    onClick={() => handleNavClick(item.href)}
                    className="text-left px-4 py-4 text-base font-semibold text-neutral-800 border-b border-neutral-100 active:bg-motor-mist rounded-lg"
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
              <div className="mt-auto p-5 border-t border-neutral-200">
                <a
                  href={links.appUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-download-app-button"
                  onClick={() => trackLead("header_download_mobile")}
                  className="flex items-center justify-center rounded-full bg-motor-red px-6 py-4 text-base font-display font-semibold text-white uppercase tracking-wide"
                >
                  {downloadCta}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
